import React from 'react';
import { motion } from 'framer-motion';

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-xl font-bold text-text-primary mb-3">{title}</h2>
    <div className="text-text-secondary leading-relaxed space-y-3">{children}</div>
  </div>
);

const Privacy = () => {
  return (
    <div className="bg-neutral-bg min-h-screen">
      {/* Hero Banner */}
      <div className="bg-primary py-16 px-4 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="text-white/70 text-sm uppercase tracking-widest mb-2">Legal</p>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">Privacy Policy</h1>
          <p className="text-white/70 text-sm">Last updated: June 1, 2025</p>
        </motion.div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>

          <div className="bg-primary/10 border border-primary/20 rounded-2xl p-5 mb-10 text-sm text-primary-dark">
            <strong>Quick summary:</strong> We collect only what we need to make the app work for you. We don't sell your data. Your videos are used only to generate your analysis. You're always in control.
          </div>

          <Section title="1. Who We Are">
            <p>Pickleball Insight Coach ("we," "us," or "our") is an AI-powered training app designed to help pickleball players improve their game. This Privacy Policy explains what data we collect, how we use it, and your rights around it.</p>
            <p>If you have questions, contact us at: <strong>privacy@pickleballinsightcoach.com</strong></p>
          </Section>

          <Section title="2. What Information We Collect">
            <p><strong>Account Information:</strong> When you create an account, we collect your name, email address, and password (stored securely and never in plain text).</p>
            <p><strong>Profile & Game Data:</strong> We collect information you provide about your skill level, playing frequency, and goals so we can personalize your experience.</p>
            <p><strong>Match Videos:</strong> When you upload or record a match, that video is processed by our AI to generate your performance analysis. Videos are stored securely and are never shared with other users or sold to third parties.</p>
            <p><strong>Usage Data:</strong> We collect information about how you use the app — which features you use, how often, and for how long — to help us improve the experience.</p>
            <p><strong>Device Information:</strong> We may collect basic device info such as device type, OS version, and app version to ensure compatibility and troubleshoot issues.</p>
          </Section>

          <Section title="3. How We Use Your Information">
            <p>We use your data to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Analyze your match videos and generate personalized insights</li>
              <li>Recommend drills and lessons based on your performance</li>
              <li>Track your progress over time</li>
              <li>Improve the accuracy of our AI models (using anonymized, aggregated data only)</li>
              <li>Send you app updates, tips, and important account notifications</li>
              <li>Respond to support requests</li>
            </ul>
            <p>We do not use your data for advertising purposes and we do not sell your personal information to any third party — ever.</p>
          </Section>

          <Section title="4. How We Share Your Information">
            <p>We only share your data in the following limited circumstances:</p>
            <p><strong>Service Providers:</strong> We work with trusted third-party vendors (such as cloud storage and analytics providers) who help us operate the app. These partners are contractually required to protect your data and may not use it for their own purposes.</p>
            <p><strong>Legal Requirements:</strong> We may disclose your information if required by law, court order, or government authority.</p>
            <p><strong>Business Transfers:</strong> If Pickleball Insight Coach is acquired or merges with another company, your data may be transferred as part of that transaction. We will notify you beforehand.</p>
          </Section>

          <Section title="5. Your Match Videos">
            <p>Your videos are yours. Here's exactly what we do with them:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Videos are uploaded securely over an encrypted connection</li>
              <li>They are processed by our AI to generate your analysis</li>
              <li>Processed videos are stored in your account for your own review</li>
              <li>We do not share your videos with other users or sell them</li>
              <li>You can delete your videos at any time from within the app</li>
            </ul>
          </Section>

          <Section title="6. Data Retention">
            <p>We keep your data for as long as your account is active. If you delete your account, we will remove your personal data within 30 days, except where we are required by law to retain it longer.</p>
            <p>You can request deletion of your account and data at any time by emailing privacy@pickleballinsightcoach.com.</p>
          </Section>

          <Section title="7. Your Rights">
            <p>Depending on where you live, you may have the following rights:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
              <li><strong>Correction:</strong> Ask us to fix inaccurate or incomplete data</li>
              <li><strong>Deletion:</strong> Request that we delete your data</li>
              <li><strong>Portability:</strong> Request your data in a portable format</li>
              <li><strong>Opt-out:</strong> Unsubscribe from marketing emails at any time</li>
            </ul>
            <p>To exercise any of these rights, email us at privacy@pickleballinsightcoach.com and we'll respond within 30 days.</p>
          </Section>

          <Section title="8. Children's Privacy">
            <p>Pickleball Insight Coach is not intended for children under 13. We do not knowingly collect personal data from children under 13. If you believe a child has provided us with personal information, please contact us and we will promptly delete it.</p>
          </Section>

          <Section title="9. Security">
            <p>We take the security of your data seriously. We use industry-standard encryption (TLS/SSL) for data in transit and AES-256 encryption for data at rest. Access to personal data within our team is strictly limited and logged.</p>
            <p>While we do everything we can to protect your information, no system is 100% secure. We encourage you to use a strong, unique password for your account.</p>
          </Section>

          <Section title="10. Cookies & Tracking">
            <p>The Pickleball Insight Coach app may use lightweight analytics tools to understand how the app is used. These tools collect anonymized, aggregated data and are never used to build advertising profiles.</p>
            <p>We do not use tracking cookies for advertising or sell data to ad networks.</p>
          </Section>

          <Section title="11. Changes to This Policy">
            <p>We may update this Privacy Policy from time to time. When we make significant changes, we'll notify you via the app or by email. The "last updated" date at the top of this page always reflects the most recent version.</p>
          </Section>

          <Section title="12. Contact Us">
            <p>If you have any questions, concerns, or requests related to your privacy, please reach out:</p>
            <p><strong>Email:</strong> privacy@pickleballinsightcoach.com</p>
            <p><strong>Mailing Address:</strong> Pickleball Insight Coach, 123 Court Lane, Suite 100, San Diego, CA 92101</p>
          </Section>

        </motion.div>
      </div>
    </div>
  );
};

export default Privacy;